import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import Loader from "@mui/material/CircularProgress";

const AbstractSubmission = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;
  const queryClient = useQueryClient()
  // Query for authenticated user
  const {
    data: authUser,
    isLoading: loadingAuthUser,
    isError: authError,
  } = useQuery({
    queryKey: ["authUser"],
  });

  // Mutation for uploading the file
  const {
    mutate: uploadAbstractMutation,
    isPending: isUploading,
    data,
  } = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("abstract", file);
      console.log("file: ",file);
      
      const response = await fetch(`${API_URL}/abstracts/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || "File upload failed");
      }      
      return data;
    },
    onSuccess: () => {
      toast.success("File uploaded successfully!");
      queryClient.invalidateQueries({queryKey: ['authUser']})
    },
    onError: (error) => {
      toast.error(error.message || "Error uploading the file");
    },
  });

  // Handle file selection
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  // Handle file upload
  const handleUpload = () => {
    if (!selectedFile) {
      toast.error("Please select a file first.");
      return;
    }
    uploadAbstractMutation(selectedFile);
  };

  // Early return for loading or error states
  if (loadingAuthUser) {
    return (
      <div className="h-[400px] flex justify-center items-center">
        <Loader sx={() => ({ color: "#4f46e5" })} />
      </div>
    );
  }

  if (authError) {
    return (
      <div className="flex items-center justify-center">
        <p className="text-red-500">
          Error loading user data. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-lg max-w-3xl mx-auto">
      <h3>
        <span className="text-lg font-semibold text-[#000] mb-4">Submission guideline: </span>
         Please download the format and prepare the abstract accordingly.
        Briefly, the guideline is given below:
      </h3>

      <p className="text-base mt-4 mb-2">
        The abstract should be in Time New Roman font, 12 font size, 1.15 line
        spacing, justified.
      </p>
      <ul className="roman-list mb-6 space-y-2">
        <li>
          <span className="font-semibold">Title:</span> Bold, please avoid
          abbreviation in title.
        </li>
        <li>
          <span className="font-semibold">Authors:</span> In case of more than
          one author, use commas to separate the names. Underline the presenting
          author. Use number superscript (i, ii, iii....) in case of more than
          one affiliation. Do not use superscript if all authors are from the
          same institute.
        </li>
        <li>
          <span className="font-semibold">Affiliation:</span> Provide postal
          address of each affiliation according to the superscript added with
          author. In case of a single institute, do not use number/bullet with
          institute address.
        </li>
        <li>
          <span className="font-semibold">Email:</span> Provide an email for
          correspondence.
        </li>
        <li>
          <span className="font-semibold">Abstract:</span> Word limit is 250. Do
          not use abbreviations at first appearance in the text. Do not use
          references in the text.
        </li>
      </ul>
      <p className="text-base mb-6">
        <span className="font-semibold">Abstract format:</span> Please click
        here to download a{" "}
        <a
          href="/Abstract-Sample-Copy.pdf"
          download
          className="text-[#4f46e5] underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          sample copy
        </a>
        .
      </p>

      <h4 className="text-lg font-semibold text-[#000] mb-4">
        Abstract submission:
      </h4>
      {authUser?.abstractSubmitted ? (
        <div className="mt-4">
          <p className="text-green-600">
            You have successfully submitted your abstract! You can view it{" "}
            <a
              href={`${process.env.REACT_APP_API_URL}/uploads/${authUser?.abstractFile}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#4f46e5] underline"
            >
              here
            </a>
            .
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4">
            <input
              type="file"
              onChange={handleFileChange}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]"
            />
          </div>

          <div className="flex justify-center mt-4">
            <button
              onClick={handleUpload}
              className="flex items-center justify-center px-4 py-2 w-28 bg-[#4f46e5] text-white font-semibold rounded-lg hover:bg-[#6a63e9]"
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader sx={() => ({ color: "#ffffff" })} size={24} />
              ) : (
                "Upload"
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AbstractSubmission;
